import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Heading, Image, Text } from '@atlassian/learning-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { getAssetUrl } from 'src/services/http/user';
import { MarketingHeroRichTitle } from 'src/graphql/generated/graphql';

interface HeadingSectionProps {
  title: string;
  richTitle?: Partial<MarketingHeroRichTitle>;
  subtitle: string;
  containerStyles?: React.CSSProperties;
  keyword: string;
  logoSrc: string;
  logoAlt: string;
  flagImageSrc?: string;
  bannerDesktopSrc: string;
  bannerMobileSrc: string;
  bannerMobileAlt: string;
}

// Utility function to render title with specified keyword in bold
const renderTitleWithBoldKeyword = (title: string, keyword: string): string | (string | React.JSX.Element)[] => {
  if (!title.includes(keyword)) {
    return title;
  }

  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');
  const parts = title.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === keyword.toLowerCase()) {
      return (
        <span id="title-keyword" key={index} style={{ fontWeight: 700 }}>
          {part}
        </span>
      );
    }

    return part;
  });
};

const parseImageSrc = (src: string): string => {
  if (!src || src.includes('http')) {
    return src;
  }

  return getAssetUrl(src);
};

const PageHeading: React.FC<HeadingSectionProps> = ({
  title,
  richTitle,
  subtitle,
  containerStyles,
  keyword,
  logoSrc,
  logoAlt,
  flagImageSrc,
  bannerDesktopSrc,
  bannerMobileSrc,
  bannerMobileAlt,
}) => {
  const titleContent = richTitle
    ? documentToReactComponents(richTitle.json)
    : renderTitleWithBoldKeyword(title, keyword);

  return (
    <div
      css={[headerContainerStyles]}
      style={
        {
          ...(flagImageSrc && { '--keyword-flag': `url(${parseImageSrc(flagImageSrc)})` }),
          '--keyword-banner': `url(${parseImageSrc(bannerDesktopSrc)})`,
          ...containerStyles,
        } as Record<string, string>
      }
    >
      <div css={paddedContainerStyles}>
        <div css={[!(logoSrc ?? '').includes('http') && logoStyles]}>
          <Image src={parseImageSrc(logoSrc)} alt={logoAlt} />
        </div>
        <Heading id={`${keyword.toLowerCase()}-adoption-hub`} level={0} fontWeight={600}>
          {titleContent}
        </Heading>
        <div css={subtitleContainerStyles}>
          <Text size="large">{subtitle}</Text>
        </div>
      </div>
      <div css={hubBannerMobileStyles}>
        <Image src={parseImageSrc(bannerMobileSrc)} alt={bannerMobileAlt} role="presentation" />
      </div>
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  paddingTop: token('space.1000'),

  backgroundImage: `var(--keyword-banner)`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',

  '& #title-keyword': {
    position: 'relative',
  },

  '& #title-keyword::after': {
    content: '""',
    display: 'inline-block',
    width: '161px',
    height: '101px',
    backgroundImage: `var(--keyword-flag, none)`,
    backgroundSize: 'contain',
    position: 'absolute',
    right: '0',
    bottom: '0',
    transform: 'translateX(calc(100% - 30px))',
  },

  [media.below.md]: {
    paddingTop: token('space.600'),

    '& #title-keyword::after': {
      display: 'none',
    },
  },

  [media.below.sm]: {
    backgroundImage: 'none',
  },
});

const subtitleContainerStyles = css({
  maxWidth: '564px',
});

const hubBannerMobileStyles = css({
  display: 'none',

  [media.below.sm]: {
    paddingTop: token('space.200'),
    display: 'block',
    width: '100%',
  },
});

const paddedContainerStyles = css({
  padding: `0 ${token('space.200')}`,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.200'),
  justifyContent: 'center',
  textAlign: 'center',
  maxWidth: '652px',
});

const logoStyles = css({
  display: 'flex',
  width: '48px',
  height: '48px',
  padding: '6px',
  justifyContent: 'center',
  alignItems: 'center',

  borderRadius: '12px',
  background: token('color.background.brand.bold'),
});

export default PageHeading;
